import React, { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  InputAdornment,
  List,
  ListItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import InventoryIcon from "@mui/icons-material/Inventory";
import Autocomplete from "@mui/material/Autocomplete";

const LinhasTabela = ({
  itens,
  atualizarItem,
  excluirItem,
  produtosMock = [],
}) => {
  const [showMenu, setShowMenu] = useState(false);

  // Formatadores e validadores
  const formatCurrency = (value) => {
    if (!value) return "R$ 0,00";
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const formatPercentage = (value) => {
    if (!value) return "0%";
    return `${value}%`;
  };

  const handleQuantidadeChange = (id, value) => {
    if (value === "") {
      atualizarItem(id, "quantidade", 0);
      return;
    }

    const numericValue = parseInt(value, 10);
    if (!isNaN(numericValue) && numericValue >= 0) {
      atualizarItem(id, "quantidade", numericValue);
    }
  };

  const handlePrecoChange = (id, event) => {
    const { value } = event.target;
    if (value === "") {
      atualizarItem(id, "precoUnitario", 0);
      return;
    }

    // Remove caracteres não numéricos exceto vírgula e converte para formato numérico
    const numeroLimpo = value.replace(/[^\d,]/g, "").replace(",", ".");
    const numericValue = parseFloat(numeroLimpo);

    if (!isNaN(numericValue) && numericValue >= 0) {
      atualizarItem(id, "precoUnitario", numericValue);
    }
  };

  const handleIPIChange = (id, event) => {
    const { value } = event.target;
    if (value === "") {
      atualizarItem(id, "ipi", 0);
      return;
    }

    // Remove caracteres não numéricos exceto vírgula e converte para formato numérico
    const numeroLimpo = value.replace(/[^\d,]/g, "").replace(",", ".");
    const numericValue = parseFloat(numeroLimpo);

    if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
      atualizarItem(id, "ipi", numericValue);
    }
  };

  // Calcular totais
  const calcularSubtotal = (quantidade, precoUnitario) => {
    const qtd = parseFloat(quantidade) || 0;
    const preco = parseFloat(precoUnitario) || 0;
    return qtd * preco;
  };

  const calcularTotal = (quantidade, precoUnitario, ipi) => {
    const subtotal = calcularSubtotal(quantidade, precoUnitario);
    const valorIPI = (subtotal * (parseFloat(ipi) || 0)) / 100;
    return subtotal + valorIPI;
  };

  return (
    <Box>
      {itens.map((item, index) => {
        const subtotal = calcularSubtotal(item.quantidade, item.precoUnitario);
        const total = calcularTotal(
          item.quantidade,
          item.precoUnitario,
          item.ipi
        );

        return (
          <Box
            key={item.id}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              p: 2,
              borderBottom: "1px solid #eee",
              "&:hover": { bgcolor: "#f9f9f9" },
            }}
          >
            {/* Índice - visível apenas em mobile */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                mb: 2,
              }}
            >
              <Typography variant="subtitle2">Item #{index + 1}</Typography>
              <IconButton
                color="error"
                size="small"
                onClick={() => excluirItem(item.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Produto */}
            <Box
              sx={{
                flex: "3",
                mb: { xs: 2, md: 0 },
                width: { xs: "100%", md: "auto" },
                mr: { xs: 0, md: 1 },
              }}
            >
              <Autocomplete
                options={produtosMock}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.nome
                }
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return option.id === value.id;
                }}
                value={item.produto}
                onChange={(event, newValue) => {
                  atualizarItem(item.id, "produto", newValue);
                }}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    sx={{ display: "flex", alignItems: "center" }}
                    {...props}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 32,
                        height: 32,
                        mr: 1,
                        borderRadius: 1,
                        bgcolor: "#f0f0f0",
                      }}
                    >
                      <InventoryIcon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="body2">{option.nome}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Cód: {option.codigo} • Preço:{" "}
                        {option.preco.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Selecionar produto"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <InventoryIcon color="action" fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* Código Fornecedor */}
            <Box
              sx={{
                flex: "1.5",
                mb: { xs: 2, md: 0 },
                width: { xs: "100%", md: "auto" },
                mx: { xs: 0, md: 1 },
              }}
            >
              <TextField
                size="small"
                label="Cód. Fornecedor"
                value={item.codigoFornecedor || ""}
                onChange={(e) =>
                  atualizarItem("codigoFornecedor", e.target.value)
                }
                InputProps={{
                  readOnly: !!item.produto,
                }}
                fullWidth
              />
            </Box>

            {/* Unidade */}
            <Box
              sx={{
                flex: "0.8",
                mb: { xs: 2, md: 0 },
                width: { xs: "100%", md: "auto" },
                mx: { xs: 0, md: 1 },
              }}
            >
              <TextField
                size="small"
                label="UN"
                value={item.unidade || ""}
                onChange={(e) =>
                  atualizarItem(item.id, "unidade", e.target.value)
                }
                InputProps={{
                  readOnly: !!item.produto,
                }}
                fullWidth
              />
            </Box>

            {/* Quantidade */}
            <Box
              sx={{
                flex: "1",
                mb: { xs: 2, md: 0 },
                width: { xs: "100%", md: "auto" },
                mx: { xs: 0, md: 1 },
              }}
            >
              <TextField
                size="small"
                label="Qtd"
                type="number"
                value={item.quantidade || ""}
                onChange={(e) =>
                  handleQuantidadeChange(item.id, e.target.value)
                }
                inputProps={{ min: 0, step: 1 }}
                fullWidth
              />
            </Box>

            {/* Preço */}
            <Box
              sx={{
                flex: "1.5",
                mb: { xs: 2, md: 0 },
                width: { xs: "100%", md: "auto" },
                mx: { xs: 0, md: 1 },
              }}
            >
              <TextField
                size="small"
                label="Preço Unit."
                value={
                  item.precoUnitario
                    ? item.precoUnitario.toString().replace(".", ",")
                    : ""
                }
                onChange={(e) => handlePrecoChange(item.id, e)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">R$</InputAdornment>
                  ),
                }}
                fullWidth
              />
            </Box>

            {/* IPI */}
            <Box
              sx={{
                flex: "1",
                mb: { xs: 2, md: 0 },
                width: { xs: "100%", md: "auto" },
                mx: { xs: 0, md: 1 },
              }}
            >
              <TextField
                size="small"
                label="IPI"
                value={item.ipi ? item.ipi.toString().replace(".", ",") : ""}
                onChange={(e) => handleIPIChange(item.id, e)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
                fullWidth
              />
            </Box>

            {/* Total */}
            <Box
              sx={{
                flex: "2",
                mb: { xs: 0, md: 0 },
                width: { xs: "100%", md: "auto" },
                display: "flex",
                flexDirection: "column",
                mx: { xs: 0, md: 1 },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: total > 0 ? "text.primary" : "text.disabled",
                }}
              >
                {formatCurrency(total)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.ipi > 0 &&
                  `(${formatCurrency(subtotal)} + ${formatPercentage(item.ipi)} IPI)`}
              </Typography>
            </Box>

            {/* Ação */}
            <Box
              sx={{
                flex: "0.5",
                display: { xs: "none", md: "flex" },
                justifyContent: "center",
                ml: { xs: 0, md: 1 },
              }}
            >
              <Tooltip title="Remover item">
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => excluirItem(item.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Botão de Tooltip */}
            <Box
              flex="1"
              textAlign="center"
              style={{
                position: "relative",
                borderLeft: "1px solid #ccc",
                borderTop: "1px solid #ccc",
                borderBottom: "1px solid #ccc",
              }}
            >
              <IconButton
                sx={{ height: "29px" }}
                onMouseEnter={() => setShowMenu(true)}
                onMouseLeave={() => setShowMenu(false)}
              >
                <InventoryIcon sx={{ fontSize: "20px", color: "#FD947C" }} />
              </IconButton>

              {/* Menu */}
              {showMenu && (
                <Box
                  onMouseEnter={() => setShowMenu(true)}
                  onMouseLeave={() => setShowMenu(false)}
                  sx={{
                    position: "absolute",
                    bottom: "100%",
                    right: 0,
                    zIndex: 10,
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    padding: "10px",
                    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                    minWidth: "200px",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", marginBottom: "5px" }}
                  >
                    Todos os depósitos
                  </Typography>
                  <List dense>
                    <ListItem>
                      <Typography variant="body2">
                        Estoque atual: 161,000
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography variant="body2">
                        Estoque mínimo: 25,000
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography variant="body2">
                        Estoque máximo: 40,000
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography variant="body2">
                        Estoque depois da compra: 162,000
                      </Typography>
                    </ListItem>
                  </List>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}

      {itens.length === 0 && (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Nenhum item adicionado ao pedido
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LinhasTabela;
